import { useEffect, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
	Stack,
	Text,
	Group,
	Button,
	Image,
	Anchor,
	Divider,
	ActionIcon,
} from "@mantine/core";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { getAssetUrl } from "@utils/getAssetUrl";
import liff from "@line/liff";

import { UserApi, WishlistResponseDTO } from "@utils/api/ec";
import { handleCollectListShareFriend, handleSendOaMessage } from "./utils";
import { useAppNavigate } from "@/apps/global/navigate";
import { useLoadingStore } from "@/stores/useLoadingStores";
import { ReservationPopup } from "@/apps/liff/pages/ec-page/components";

import { useUserEvent } from "@/apps/@hooks/useUserEvent";
import { useChooseStoreOpen } from "@/apps/liff/pages/ec-page/hook/useChooseStoreOpen";
import { useWaitingInPage } from "@/apps/@hooks/useWaitingInPage";

export default function EcCollectPage() {
	const { navigateToEcHome, navigateToEcProduct } = useAppNavigate();
	const { pathname } = useLocation();
	const { code } = useParams();
	const [searchParams] = useSearchParams();
	const trackingId = searchParams.get("td");
	const { logPage, logEvent } = useUserEvent();
	const isFromLiffShare = pathname.includes("/from-liff-share");

	const [page, setPage] = useState(1);
	const [wishlist, setWishlist] = useState<WishlistResponseDTO[]>([]);
	const [togglingMap, setTogglingMap] = useState<Record<number, boolean>>({});
	const [myShareCode, setMyShareCode] = useState<string | null>(null);
	const [nickname, setNickname] = useState<string | null>(null);
	const [onlineOpen, setOnlineOpen] = useState(false);
	const { isLoading, setIsLoading } = useLoadingStore();
	const { setChooseStoreOpen, setChooseStoreStyleCode, setChooseStoreType } =
		useChooseStoreOpen();

	const pageSize = 4;
	const totalPages = Math.ceil(wishlist.length / pageSize);
	const pageItems = wishlist.slice((page - 1) * pageSize, page * pageSize);
	const [isPageReady, setIsPageReady] = useState(false);

	const goPrev = () => {
		if (page > 1) {
			setPage(page - 1);
		}
	};

	const goNext = () => {
		if (page < totalPages) {
			setPage(page + 1);
		}
	};

	const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
	const refreshWishlist = async () => {
		setIsLoading(true);
		try {
			const [res] = await Promise.all([UserApi.getWishlist(), sleep(1500)]);
			setWishlist(res.data);
		} catch (error) {
			console.error("取得最愛清單失敗", error);
		} finally {
			setIsPageReady(true);
			setIsLoading(false);
		}
	};
	const fromLiffShareWishlist = async () => {
		setIsLoading(true);
		try {
			if (!code) {
				navigateToEcHome();
				return;
			}
			const [res] = await Promise.all([
				UserApi.getFromLiffShareWishlist(code),
				sleep(1500),
			]);
			if (res.data.collect.length === 0) {
				navigateToEcHome();
				return;
			}
			setWishlist(res.data.collect);
			setNickname(res.data.nickname);
		} catch (error) {
			console.error("取得最愛清單失敗", error);
		} finally {
			setIsPageReady(true);
			setIsLoading(false);
		}
	};
	const getMyShareCode = async () => {
		const res = await UserApi.getShareCode();
		if (res.data.code) {
			setMyShareCode(res.data.code);
		}
	};
	const handleToggleCollection = async (id: number, nextStatus: boolean) => {
		if (togglingMap[id]) {
			return;
		}
		try {
			if (nextStatus) {
				logEvent({
					category: "cta",
					path: "/wishlist",
					action: "like",
					event: "cta_like",
					value: id.toString(),
				});
			} else {
				logEvent({
					category: "cta",
					path: "/wishlist",
					action: "unlike",
					event: "cta_unlike",
					value: id.toString(),
				});
			}
			setTogglingMap((prev) => ({ ...prev, [id]: true }));
			await UserApi.toggleProductCollection(id, nextStatus);
			setTogglingMap((prev) => ({ ...prev, [id]: false }));

			setWishlist((prev) =>
				prev.map((it) =>
					it.id === id ? { ...it, collection_status: nextStatus } : it,
				),
			);
		} catch (error) {
			console.error("切換收藏狀態失敗", error);
		}
	};
	const handleConsult = async () => {
		setChooseStoreStyleCode("consult");
		setChooseStoreType("consult");
		return handleSendOaMessage({
			isLoading,
			setIsLoading,
			logEvent,
			log: {
				category: "cta",
				path: "/wishlist",
				action: "buy",
				event: "cta_buy",
			},
			styleCode: "consult",
			type: "consult",
			onSuccess: () => setOnlineOpen(true),
			setIsChooseStoreOpen: setChooseStoreOpen,
		});
	};
	const handleOnlineClose = () => {
		liff.openWindow({
			url: process.env.REACT_APP_LINE_OA_URL ?? "",
			external: false,
		});
	};

	useEffect(() => {
		if (isFromLiffShare) {
			fromLiffShareWishlist();
			return;
		}
		refreshWishlist();
		if (!myShareCode) {
			getMyShareCode();
		}
	}, []);

	useEffect(() => {
		logPage({
			path: "/wishlist",
		});
	}, []);

	useEffect(() => {
		if (!trackingId) {
			return;
		}
		logEvent({
			category: "mgm",
			path: "/wishlist",
			action: "flex_list_share_friend_clicked",
			event: "mgm_share_friend_clicked",
			value: trackingId,
		});
	}, [trackingId]);

	useWaitingInPage({
		enabled: isPageReady, // true / false
		getPath: () => `/wishlist`,
		logEvent,
	});

	return (
		<Stack gap="0" flex={1}>
			<Stack gap="0" flex={1}>
				<Text size="3.09vw" py="5.67vw" ta="center" fw="500">
					{nickname ? `${nickname}的最愛清單` : "我的最愛清單"}
				</Text>

				<Stack gap="0" flex={1}>
					{pageItems.length > 0 ? (
						<Stack gap="0" flex={1} justify="space-between">
							<Stack gap="0">
								{pageItems.map((item) => (
									<Group
										key={item.id}
										p="5.15vw"
										gap="0"
										w="100%"
										wrap="nowrap"
										align="start"
										justify="space-between"
									>
										<Group gap="3.87vw" align="start">
											<Image
												src={item.images_url?.[0]}
												alt={item.name}
												h="24.48vw"
												w="21.9vw"
											/>
											<Stack miw={0} flex={1}>
												<Text 
													size="3.09vw" 
													c="#6E727C"
													lh="1.2"
													style={{
														wordBreak: 'break-word',
														whiteSpace: 'normal',
														overflowWrap: 'break-word'
													}}
												>
													{item.name}
												</Text>
												<Text c="#6C717A" size="2.58vw" ta="left">
													顏色：{item.color}
												</Text>
											</Stack>
										</Group>

										<Stack h="24.48vw" align="end">
											<div style={{ width: "4.12vw", height: "3.61vw" }}>
												<Image
													src={getAssetUrl(
														item.collection_status
															? "/images/ec-campaign/active-love-icon.png"
															: "/images/ec-campaign/love-icon.png",
													)}
													alt="wish-icon"
													w="100%"
													h="100%"
													fit="contain"
													style={{ display: "block" }}
													onClick={(e) => {
														e.stopPropagation();
														if (togglingMap?.[item.id]) {
															return;
														}
														handleToggleCollection(
															item.id,
															!item.collection_status,
														);
													}}
												/>
											</div>
											<div style={{ flex: 1 }} />

											<Stack gap="0" align="end" style={{ flexShrink: 0 }}>
												<Anchor
													size="3.09vw"
													c="black"
													underline="always"
													style={{ whiteSpace: 'nowrap' }}
													onClick={() => {
														logEvent({
															category: "cta",
															path: "/wishlist",
															action: "view",
															event: "cta_view",
															value: item.id.toString(),
														});
														navigateToEcProduct(item.id);
													}}
												>
													前往商品頁
												</Anchor>
												<Divider c="#E2E4E7" />
											</Stack>
										</Stack>
									</Group>
								))}
							</Stack>

							<Group justify="center" align="center" gap="2.06vw" py="5.15vw">
								{page > 1 && (
									<ActionIcon
										onClick={goPrev}
										variant="subtle"
										aria-label="prev-page"
									>
										<CaretLeftIcon size="4.5vw" color="black" />
									</ActionIcon>
								)}

								{Array.from({ length: totalPages }, (_, i) => i + 1).map(
									(n, idx) => (
										<Group key={n} gap="2.06vw" align="center">
											<Anchor
												onClick={() => setPage(n)}
												c={page === n ? "#2E3238" : "#6E727C"}
												size="3vw"
												style={{ fontWeight: page === n ? 600 : 400 }}
											>
												{n}
											</Anchor>
											{idx < totalPages - 1 && (
												<Text c="#2E3238" size="3.09vw">
													|
												</Text>
											)}
										</Group>
									),
								)}

								{page < totalPages && (
									<ActionIcon
										onClick={goNext}
										variant="subtle"
										aria-label="next-page"
									>
										<CaretRightIcon size="4.5vw" color="black" />
									</ActionIcon>
								)}
							</Group>
						</Stack>
					) : (
						<Stack flex={1} justify="center" align="center" gap="5.15vw">
							<Image
								src={getAssetUrl("/images/ec-campaign/tb-logo.png")}
								alt="empty-list"
								h="10.31vw"
								w="10.31vw"
							/>
							<Text size="3.61vw" ta="center" fw="500">
								你尚未建立最愛清單
							</Text>
						</Stack>
					)}

					{!isFromLiffShare && (
						<Group
							justify="center"
							align="center"
							px="5.15vw"
							pb="5.15vw"
							mt="auto"
						>
							{pageItems.length > 0 ? (
								<Stack gap="2.58vw" w="100%">
									<Button
										color="black"
										h="9.02vw"
										w="100%"
										fz="2.58vw"
										onClick={handleConsult}
									>
										線上諮詢購買
									</Button>
									<Button
										color="black"
										h="9.02vw"
										w="100%"
										fz="2.58vw"
										onClick={() => {
											if (!myShareCode) {
												getMyShareCode();
												return;
											}
											let shareTracking = uuidv4().toString();
											logEvent({
												category: "cta",
												path: "/wishlist",
												action: "share_list",
												event: "cta_share_list",
											});
											logEvent({
												category: "mgm",
												path: "/wishlist",
												action: "flex_list_share",
												event: "mgm_share",
												value: shareTracking,
											});
											handleCollectListShareFriend(myShareCode, shareTracking);
										}}
									>
										分享最愛清單
									</Button>
								</Stack>
							) : (
								<Button
									color="black"
									h="9.02vw"
									w="100%"
									fz="2.58vw"
									onClick={() => {
										logEvent({
											category: "cta",
											path: "/wishlist",
											action: "start_collect",
											event: "cta_start_collect",
										});
										navigateToEcHome();
									}}
								>
									開始收藏
								</Button>
							)}
						</Group>
					)}
				</Stack>
			</Stack>
			<ReservationPopup
				opened={onlineOpen}
				onClose={() => setOnlineOpen(false)}
				onConfirm={handleOnlineClose}
			/>
		</Stack>
	);
}
